/**
 * Add a business (v10.0.0): its name and state, in the same bottom sheet as the
 * rest of the app (focus trap, Escape, backdrop). Creates the entity and its
 * Unallocated envelope on the server; no money moves.
 */

import { trapFocus } from '../lib/dialog-trap';
import { US_STATES } from '../lib/us-states';

export interface BusinessSheetOptions {
  onSubmit: (name: string, state: string) => Promise<void>;
  onDismiss: () => void;
  describeError: (err: unknown) => string;
}

export function createBusinessSheet(opts: BusinessSheetOptions): HTMLElement {
  const backdrop = document.createElement('div');
  backdrop.className = 'sheet-backdrop';

  const sheet = document.createElement('div');
  sheet.className = 'sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', 'business-sheet-title');

  const title = document.createElement('h2');
  title.id = 'business-sheet-title';
  title.className = 'sheet-title';
  title.textContent = 'Add your business';

  const sub = document.createElement('p');
  sub.className = 'sheet-sub';
  sub.textContent =
    'Each business keeps its own envelopes and its own accounts. Nothing is shared between them.';

  const form = document.createElement('form');
  form.className = 'sheet-form';
  form.noValidate = true;

  const nameLabel = document.createElement('label');
  nameLabel.className = 'sheet-field-label';
  nameLabel.htmlFor = 'business-name';
  nameLabel.textContent = 'Business name';
  const name = document.createElement('input');
  name.className = 'sheet-input';
  name.id = 'business-name';
  name.type = 'text';
  name.maxLength = 80;
  name.autocomplete = 'organization';
  name.autocapitalize = 'words';

  const stateLabel = document.createElement('label');
  stateLabel.className = 'sheet-field-label';
  stateLabel.htmlFor = 'business-state';
  stateLabel.textContent = 'State';
  const state = document.createElement('select');
  state.className = 'sheet-input';
  state.id = 'business-state';
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = 'Choose a state';
  state.append(placeholder);
  for (const code of US_STATES) {
    const o = document.createElement('option');
    o.value = code;
    o.textContent = code;
    state.append(o);
  }

  const note = document.createElement('p');
  note.className = 'sheet-note soft';
  note.textContent = 'The state decides which set-aside rates apply. You set the rates next.';

  const message = document.createElement('p');
  message.className = 'sheet-message';
  message.setAttribute('role', 'alert');

  const actions = document.createElement('div');
  actions.className = 'sheet-actions';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn-quiet';
  cancel.textContent = 'Not now';
  cancel.addEventListener('click', () => opts.onDismiss());
  const confirm = document.createElement('button');
  confirm.type = 'submit';
  confirm.className = 'btn-primary';
  confirm.textContent = 'Add business';
  confirm.disabled = true;
  actions.append(cancel, confirm);

  const refreshEnabled = () => {
    confirm.disabled = name.value.trim() === '' || state.value === '';
  };
  for (const el of [name, state]) {
    el.addEventListener('input', () => {
      message.textContent = '';
      refreshEnabled();
    });
    el.addEventListener('change', refreshEnabled);
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (confirm.disabled) return;
    confirm.disabled = true;
    confirm.textContent = 'Adding…';
    try {
      await opts.onSubmit(name.value.trim(), state.value);
    } catch (err) {
      message.textContent = opts.describeError(err);
      confirm.textContent = 'Add business';
      refreshEnabled();
    }
  });

  form.append(nameLabel, name, stateLabel, state, note, message, actions);
  sheet.append(title, sub, form);
  backdrop.append(sheet);
  trapFocus({ backdrop, sheet, onDismiss: opts.onDismiss });
  queueMicrotask(() => name.focus());
  return backdrop;
}
